import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgendaStore } from '../store/agendaStore';
import { useWizardStore } from '../store/wizardStore';
import {
  fetchTerms,
  fetchAvailability,
  reserveDay,
  confirmReservation,
  termsText,
  type DisplayDay,
  type InterventionTerms,
} from '../services/agenda';

// E1 customer-side agenda. PAY first (upstream) → land here → accept T&C →
// pick a day from the calendar → reserve+confirm (already paid) → verification.
//
// The stage components are declared at MODULE level (not nested inside
// AgendaPage). Nesting them would re-create the function on every store update,
// which React treats as a new component type → remount → local state (selected
// month, selected day) would silently reset. Hoisting keeps their state stable.

const WEEKDAYS_PT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// Monday-start grid of ISO date strings (null = padding cell) for the month.
function buildMonthGrid(year: number, month0: number): (string | null)[] {
  const first = new Date(year, month0, 1);
  // JS getDay: 0=Sun..6=Sat → shift to Monday-start (0=Mon..6=Sun).
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${year}-${pad2(month0 + 1)}-${pad2(d)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatDatePT(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  // Clamp the month index so a corrupt/stale persisted date renders a plausible
  // string instead of "5 de undefined de 2026".
  const monthName = MONTHS_PT[Math.min(11, Math.max(0, (m ?? 1) - 1))];
  return `${d ?? '?'} de ${monthName} de ${y ?? '?'}`;
}

export function AgendaPage() {
  const { paymentRef, tierSnapshot, termsAccepted, interventionCode, selectedDate, status, stage, setPaymentRef, setTierSnapshot } =
    useAgendaStore();

  const navigate = useNavigate();
  const installTier = useWizardStore((s) => s.installTier);

  // ?ref= capture (payment/order reference from the checkout return) + snapshot
  // the install tier once, so later cart-tier edits can't mutate agenda semantics.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ref = q.get('ref');
    if (ref && ref !== paymentRef) setPaymentRef(ref);
    if (tierSnapshot == null) setTierSnapshot(installTier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The tier that governs this agenda flow: the snapshot taken on entry, not the
  // live wizard value (which the customer can still change from the cart).
  const effectiveTier = tierSnapshot ?? installTier;

  // A simulated (mock) quote reaches here with a MOCK-* reference — no real
  // payment happened. Keep the copy honest instead of asserting "Pagamento
  // confirmado". (Whether mock quotes may reach the live agenda at all is a
  // product decision left to Rafiq.)
  const isSimulated = (paymentRef ?? '').toUpperCase().startsWith('MOCK');

  // P0-1 gate: "Apenas material" has no installation to schedule. Skip the whole
  // flow with a clear message instead of showing an irrelevant calendar.
  if (effectiveTier === 'material' && stage !== 'verifying') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-smartify border border-smartify-line bg-white p-6 text-center">
          <p className="text-3xl">📦</p>
          <h1 className="mt-2 text-lg font-bold text-smartify-ink">Sem instalação a agendar</h1>
          <p className="mt-1 text-sm text-smartify-muted">
            Escolheste <b>Apenas material</b> — o teu pedido segue para envio/levantamento, sem
            visita de instalação. Se quiseres instalação profissional, volta ao orçamento e
            escolhe um serviço com instalação.
          </p>
          <button
            type="button"
            onClick={() => navigate('/cart')}
            className="mt-4 rounded-smartify border border-smartify-line px-5 py-2.5 text-sm font-medium text-smartify-ink hover:border-smartify-brand"
          >
            ← Voltar ao orçamento
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <header className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-smartify-muted">Agendamento da instalação</p>
        <h1 className="mt-1 text-xl font-bold text-smartify-ink">Marca a tua intervenção</h1>
        <p className="mt-1 text-sm text-smartify-muted">
          {isSimulated
            ? 'Escolhe o dia em que queres receber a nossa equipa. A marcação fica sujeita a confirmação.'
            : 'Pagamento confirmado. Escolhe o dia em que queres receber a nossa equipa.'}
        </p>
        {isSimulated && (
          <p className="mt-2 rounded-smartify border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Orçamento em modo simulação — sem pagamento confirmado. A marcação será validada
            manualmente pela nossa equipa antes de ficar definitiva.
          </p>
        )}
      </header>

      {stage === 'terms' && <TermsStage />}
      {stage === 'calendar' && termsAccepted && <CalendarStage />}
      {stage === 'verifying' && interventionCode && (
        <VerifyingStage date={selectedDate} code={interventionCode} status={status} />
      )}
    </div>
  );
}

// ── Stage 1: Terms & Conditions ───────────────────────────────────────────────
function TermsStage() {
  const acceptTerms = useAgendaStore((s) => s.acceptTerms);
  const [terms, setTerms] = useState<InterventionTerms | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTerms()
      .then((t) => { if (!cancelled) setTerms(t); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attempt]);

  return (
    <div className="space-y-4">
      <div className="rounded-smartify border border-smartify-line bg-white p-4">
        <h2 className="mb-2 font-semibold text-smartify-ink">Termos e condições do serviço</h2>
        {loading && <p className="text-sm text-smartify-muted">A carregar os termos…</p>}
        {error && !loading && (
          <div className="space-y-3">
            <p className="text-sm text-red-700">{error}</p>
            <p className="text-sm text-smartify-muted">
              Não conseguimos carregar a agenda neste momento, por isso a marcação online
              está temporariamente indisponível. Tenta novamente daqui a pouco — ou avança
              sem marcação: a nossa equipa contacta-te para agendar a instalação.
            </p>
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="rounded-smartify border border-smartify-line px-4 py-2 text-sm font-medium text-smartify-ink hover:border-smartify-brand"
            >
              Tentar novamente
            </button>
          </div>
        )}
        {!loading && !error && (
          <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-smartify bg-smartify-paper p-3 text-sm leading-relaxed text-smartify-ink">
            {termsText(terms)
              || 'Ao agendar, aceitas que a equipa Smartify realize a intervenção na morada indicada. A marcação fica sujeita a verificação e confirmação pela nossa equipa. Eventuais trabalhos adicionais identificados no local serão orçamentados à parte antes de avançar.'}
          </div>
        )}
      </div>

      {/* The accept + continue controls only make sense when the terms actually
          loaded — with the service down they led to a silent dead-end. */}
      {!error && (
        <>
          <label className="flex cursor-pointer items-start gap-3 rounded-smartify border border-smartify-line bg-white p-3">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-smartify-line text-smartify-brand focus:ring-smartify-brand"
            />
            <span className="text-sm text-smartify-ink">
              Li e aceito os termos e condições do serviço de instalação.
            </span>
          </label>

          <button
            type="button"
            disabled={!checked || loading}
            onClick={() => acceptTerms(terms?.version ?? null)}
            className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continuar para o calendário
          </button>
        </>
      )}
    </div>
  );
}

// ── Stage 2: Calendar ─────────────────────────────────────────────────────────
function CalendarStage() {
  const { paymentRef, selectedDate, reservedCode, setReserved, setSelectedDate, setBooked, setStage } = useAgendaStore();
  const today = isoToday();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month0, setMonth0] = useState(now.getMonth());

  const [days, setDays] = useState<Record<string, DisplayDay>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const grid = useMemo(() => buildMonthGrid(year, month0), [year, month0]);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    setError(null);
    const monthStart = `${year}-${pad2(month0 + 1)}-01`;
    // Don't ask SYW for past days of the current month (and stay safe if SYW ever
    // rejects from < today): clamp the range start to today.
    const from = monthStart < today ? today : monthStart;
    const last = new Date(year, month0 + 1, 0).getDate();
    const to = `${year}-${pad2(month0 + 1)}-${pad2(last)}`;
    try {
      const list = await fetchAvailability(from, to);
      const map: Record<string, DisplayDay> = {};
      for (const d of list) map[d.date] = d;
      setDays(map);
    } catch (e) {
      setError((e as Error).message);
      setDays({});
    } finally {
      setLoading(false);
    }
  }, [year, month0, today]);

  useEffect(() => { void loadMonth(); }, [loadMonth]);

  const atCurrentMonth = year === now.getFullYear() && month0 === now.getMonth();
  // Any bookable day in the currently-rendered month?
  const hasBookable = useMemo(
    () => grid.some((iso) => iso != null && !cellState(iso).disabled),
    // cellState reads days/today; recompute when the month's availability changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grid, days],
  );

  function prevMonth() {
    if (atCurrentMonth) return;
    setSelectedDate(null);
    if (month0 === 0) { setYear((y) => y - 1); setMonth0(11); }
    else setMonth0((m) => m - 1);
  }
  function nextMonth() {
    setSelectedDate(null);
    if (month0 === 11) { setYear((y) => y + 1); setMonth0(0); }
    else setMonth0((m) => m + 1);
  }

  function cellState(iso: string): { day: DisplayDay | null; disabled: boolean } {
    if (iso < today) return { day: null, disabled: true };
    const day = days[iso] ?? null;
    const disabled = !day || !day.bookable;
    return { day, disabled };
  }

  const confirmBooking = useCallback(async () => {
    if (!selectedDate) return;
    setBooking(true);
    setNotice(null);
    try {
      // Resume path: a prior attempt already reserved (hold code persisted) but
      // confirm didn't land. Re-confirm that hold instead of reserving again —
      // re-reserving would 409 against the customer's own hold.
      let code = reservedCode;
      if (!code) {
        const reserved = await reserveDay({ date: selectedDate, ref: paymentRef ?? undefined });
        if (!reserved.ok) {
          // 409 = the real gate. Refresh availability and ask for another day.
          setNotice(reserved.message);
          setSelectedDate(null);
          await loadMonth();
          return;
        }
        code = reserved.data.code;
        // Persist the hold IMMEDIATELY, before confirm, so a failure here can't
        // strand a hold the app no longer knows about.
        setReserved(code);
      }
      // Already paid → confirm → AGUARDA_VERIFICACAO. setBooked clears reservedCode.
      const confirmed = await confirmReservation(code);
      setBooked(confirmed.code, confirmed.status, selectedDate);
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBooking(false);
    }
  }, [selectedDate, paymentRef, reservedCode, loadMonth, setReserved, setSelectedDate, setBooked]);

  return (
    <div className="space-y-4">
      {/* Month switcher */}
      <div className="flex items-center justify-between rounded-smartify border border-smartify-line bg-white px-3 py-2">
        <button
          type="button"
          onClick={prevMonth}
          disabled={atCurrentMonth}
          className="rounded-smartify px-3 py-1.5 text-sm text-smartify-ink hover:bg-smartify-paper disabled:opacity-30"
          aria-label="Mês anterior"
        >
          ←
        </button>
        <span className="font-semibold text-smartify-ink">{MONTHS_PT[month0]} {year}</span>
        <button
          type="button"
          onClick={nextMonth}
          className="rounded-smartify px-3 py-1.5 text-sm text-smartify-ink hover:bg-smartify-paper"
          aria-label="Mês seguinte"
        >
          →
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-smartify-muted">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-smartify-brand" /> Disponível</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Poucas vagas</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-smartify-line" /> Indisponível</span>
      </div>

      <div className="rounded-smartify border border-smartify-line bg-white p-3">
        <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-smartify-muted">
          {WEEKDAYS_PT.map((w) => <div key={w} className="py-1">{w}</div>)}
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-smartify-muted">A carregar disponibilidades…</div>
        ) : error ? (
          <div className="py-8 text-center">
            <p className="text-sm text-red-700">{error}</p>
            <button
              type="button"
              onClick={() => void loadMonth()}
              className="mt-3 rounded-smartify border border-smartify-line px-4 py-2 text-sm text-smartify-ink hover:border-smartify-brand"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {grid.map((iso, i) => {
              if (!iso) return <div key={`pad-${i}`} />;
              const dayNum = Number(iso.slice(-2));
              const { day, disabled } = cellState(iso);
              const selected = selectedDate === iso;
              const poucas = day?.status === 'poucas-vagas';
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={disabled}
                  onClick={() => setSelectedDate(iso)}
                  title={poucas ? 'Poucas vagas' : undefined}
                  className={[
                    'relative aspect-square rounded-smartify text-sm transition-colors',
                    selected
                      ? 'bg-smartify-brand font-semibold text-white'
                      : disabled
                        ? 'cursor-not-allowed text-smartify-muted/40'
                        : poucas
                          ? 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                          : 'text-smartify-ink hover:bg-smartify-paper',
                  ].join(' ')}
                >
                  {dayNum}
                  {!selected && !disabled && (
                    <span
                      className={[
                        'absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full',
                        poucas ? 'bg-amber-400' : 'bg-smartify-brand',
                      ].join(' ')}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!loading && !error && !hasBookable && (
        <div className="rounded-smartify border border-smartify-line bg-smartify-paper p-3 text-center text-sm text-smartify-muted">
          Sem vagas disponíveis em {MONTHS_PT[month0]}. Experimenta o mês seguinte →
        </div>
      )}

      {notice && (
        <div className="rounded-smartify border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          {notice}
        </div>
      )}

      {selectedDate && (
        <div className="rounded-smartify border border-smartify-line bg-white p-4">
          <p className="text-sm text-smartify-muted">Dia escolhido</p>
          <p className="mb-3 font-semibold text-smartify-ink">{formatDatePT(selectedDate)}</p>
          <button
            type="button"
            disabled={booking}
            onClick={() => void confirmBooking()}
            className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {booking ? 'A marcar…' : 'Confirmar marcação'}
          </button>
          <p className="mt-2 text-center text-xs text-smartify-muted">
            A marcação fica sujeita a verificação pela nossa equipa.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setStage('terms')}
        className="block w-full text-center text-xs text-smartify-muted hover:text-smartify-ink"
      >
        ← Voltar aos termos
      </button>
    </div>
  );
}

// ── Stage 3: Verifying ─────────────────────────────────────────────────────────
function VerifyingStage({ date, code, status }: { date: string | null; code: string; status: string | null }) {
  return (
    <div className="space-y-4">
      <div className="rounded-smartify border border-green-200 bg-green-50 p-5 text-center">
        <p className="text-3xl">📋</p>
        <h2 className="mt-2 font-semibold text-green-800">Marcação registada — em verificação</h2>
        <p className="mt-1 text-sm text-green-700">
          Recebemos o teu pedido. A nossa equipa vai verificar a disponibilidade e
          confirmar a marcação por email/telefone. Não precisas de fazer mais nada.
        </p>
      </div>

      <div className="rounded-smartify border border-smartify-line bg-white p-4 space-y-2 text-sm">
        {date && (
          <div className="flex items-center justify-between">
            <span className="text-smartify-muted">Dia pedido</span>
            <span className="font-medium text-smartify-ink">{formatDatePT(date)}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-smartify-muted">Referência</span>
          <span className="font-mono text-smartify-ink">{code}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-smartify-muted">Estado</span>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            {status === 'AGUARDA_VERIFICACAO' ? 'Aguarda verificação' : (status ?? 'Aguarda verificação')}
          </span>
        </div>
      </div>

      <p className="text-center text-xs text-smartify-muted">
        Guarda a referência acima — vais precisar dela se nos contactares sobre esta marcação.
      </p>
    </div>
  );
}
